import React from "react";
import { format2 } from "../../helpers/helper";
import { Box, Typography, Skeleton } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { useSalesVsBudget } from "../../hooks/useSalesVsBudget";

function RetailSalesCard({ dateParams, isDateRangeIncomplete }) {
  const {
    data,
    achievedPercent,
    deltaPercent,
    isLoading,
    error,
  } = useSalesVsBudget({
    categoryId: "RET",
    dateParams,
    isDateRangeIncomplete,
  });

  const isPositive = deltaPercent >= 100;
  const DeltaIcon = isPositive ? TrendingUpIcon : TrendingDownIcon;

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: 110,
          p: { xs: 2.5, lg: 2 },
          borderRadius: "20px",
          bgcolor: "background.paper",
          boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
        }}
      >
        <Skeleton variant="text" width={160} height={16} />
        <Skeleton variant="text" width={80} height={28} />
        <Skeleton variant="text" width={120} height={14} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: 110,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          borderRadius: "20px",
          bgcolor: "background.paper",
        }}
      >
        <Typography sx={{ color: "error.main", fontSize: 12 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (isDateRangeIncomplete) {
    return (
      <Box
        sx={{
          minHeight: 110,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          borderRadius: "20px",
          bgcolor: "background.paper",
        }}
      >
        <Typography sx={{ color: "warning.main", fontSize: 12 }}>
          Select both From & To dates
        </Typography>
      </Box>
    );
  }

  if (data.achieved === 0 && data.budget === 0) {
    return (
      <Box
        sx={{
          minHeight: 110,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
          borderRadius: "20px",
          bgcolor: "background.paper",
        }}
      >
        <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
          No retail sales data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 110,
        p: { xs: 2.5, lg: 1.5, xl: 2 },
        borderRadius: "20px",
        bgcolor: "background.paper",
        boxShadow: "0px 6px 20px rgba(0,0,0,0.35)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        transition: "all 0.2s ease",
        "&:hover": { transform: "translateY(-2px)" },
      }}
    >
      <Typography 
        sx={{ 
          fontSize: { xs: 12, lg: 11, xl: 14 }, 
          color: "secondary.main",
          whiteSpace: "nowrap" 
        }}
      >
        Retail Sales Achieved – {data.year}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: { lg: "4px", xl: "8px" }
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 24, lg: 15, xl: 17 },
            fontWeight: 700,
            color: "text.primary",
            whiteSpace: "nowrap"
          }}
        >
          {format2(achievedPercent)} %
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            fontSize: { xs: 12, lg: 10, xl: 12 },
            color: isPositive ? "#22C55E" : "#EF4444",
            whiteSpace: "nowrap"
          }}
        >
          {format2(Math.abs(deltaPercent))} %
          <DeltaIcon sx={{ fontSize: "inherit" }} />
          <Typography 
            component="span" 
            sx={{ 
              fontSize: "inherit",
              whiteSpace: "nowrap"
            }}
          >
            vs pro rata
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(RetailSalesCard);